import React, { useCallback, useContext, useEffect, useState } from 'react';
import { observer } from 'mobx-react';

import { theme } from '../../styles';
import * as S from './PromptManage.style';

import Image from '../../components/image/Image';
import AttendanceTable from './promptAttendanceTable/PromptAttendanceTable';
import TotalAttendance from './promptTotalAttendance/PromptTotalAttendance';

import { useDate } from '../../hooks/useDate';

// 페이징 쿼리로 변경
import useAttendancePagingQuery from '../../hooks/queries/useAttendancePagingQuery';
import UserInfo from './promptUserInfo/PromptUserInfo';
import { RootStoreContext } from '../../App';
import Spinner from '../../components/spinner/Spinner';
import Pagination from '../../components/pagination/Pagination';
import { deleteUser } from '../../services/apis/UserApi';
import { deleteUserFace, getUserFace } from '../../services/apis/UserFaceApi';

// Debounce hook 추가
const useDebounce = (value, delay) => {
	const [debouncedValue, setDebouncedValue] = useState(value);

	useEffect(() => {
		const handler = setTimeout(() => {
			setDebouncedValue(value);
		}, delay);

		return () => {
			clearTimeout(handler);
		};
	}, [value, delay]);

	return debouncedValue;
};

const ATTENDANCE_STATE = {
	ATTENDANCE: {
		name: '출석',
		color: theme.color.grass,
	},
	ABSENCE: {
		name: '결석',
		color: theme.color.gold,
	},
};

const PromptManagePage = observer(() => {
	const { authStore } = useContext(RootStoreContext);
	const date = useDate();

	// 페이징 상태
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(20);
	const [searchInput, setSearchInput] = useState('');
	const [searchType, setSearchType] = useState('name');
	const [isDeletingSelected, setIsDeletingSelected] = useState(false);
	const [selectedUserIds, setSelectedUserIds] = useState([]);

	const debouncedSearch = useDebounce(searchInput, 500);
	const nameFilter = searchType === 'name' ? debouncedSearch : '';
	const classFilter = searchType === 'class' ? debouncedSearch : '';

	const {
		data: attendance,
		refetch,
		loading,
		pageInfo,
		collection,
	} = useAttendancePagingQuery(
		date.getFormatDate('yyyy,MM,dd'),
		currentPage,
		pageSize,
		nameFilter,
		classFilter,
	);

	const [user, setUser] = useState();

	// 현재 페이지 데이터가 바뀌면 선택값 정리
	useEffect(() => {
		const currentIds = new Set(
			(attendance || []).map((item) => String(item.id)),
		);
		setSelectedUserIds((prev) => {
			const next = prev.filter((selectedId) =>
				currentIds.has(String(selectedId)),
			);
			if (
				next.length === prev.length &&
				next.every((id, index) => id === prev[index])
			) {
				return prev;
			}
			return next;
		});
	}, [attendance]);

	const setDays = useCallback(
		(isNext) => {
			date.setDays(isNext);
			setCurrentPage(1);
		},
		[date],
	);

	const setMonth = useCallback(
		(isNext) => {
			date.setMonths(isNext);
			setCurrentPage(1);
		},
		[date],
	);

	const getFilenameFromURL = (url) => {
		const name = decodeURI(url.split('/').pop());
		return name;
	};

	const saveBlobAsFile = (blob, filename) => {
		const link = document.createElement('a');
		link.href = URL.createObjectURL(blob);
		link.download = filename;
		link.click();
	};

	const downloadPrivacy = async (url) => {
		try {
			const response = await fetch(url);
			const blob = await response.blob();
			const filename = getFilenameFromURL(url);
			saveBlobAsFile(blob, filename);
		} catch (error) {
			alert('다운로드를 실패했습니다.');
			console.error(error);
		}
	};

	const handlePageChange = (newPage) => {
		setCurrentPage(newPage);
	};

	const handlePageSizeChange = (e) => {
		const newSize = parseInt(e.target.value, 10);
		setPageSize(newSize);
		setCurrentPage(1);
	};

	const handleDeleteSelectedUsers = async () => {
		if (isDeletingSelected) return;
		if (!authStore?.auth?.collectionName) {
			alert('컬렉션 정보가 없어 삭제를 진행할 수 없습니다.');
			return;
		}

		const selectedUsers = (attendance || []).filter((item) =>
			selectedUserIds.includes(String(item.id)),
		);

		if (!selectedUsers.length) {
			alert('삭제할 친구를 선택해 주세요.');
			return;
		}

		if (
			!window.confirm(
				`선택한 ${selectedUsers.length}명의 친구 정보를 삭제하시겠습니까?`,
			)
		) {
			return;
		}

		setIsDeletingSelected(true);
		try {
			let failedCount = 0;
			for (const targetUser of selectedUsers) {
				try {
					const faces = await getUserFace({
						faceUserId: targetUser.id,
						collectionName: authStore.auth.collectionName,
					});

					if (Array.isArray(faces)) {
						for (const face of faces) {
							if (face?.id) {
								await deleteUserFace({ id: face.id });
							}
						}
					}

					await deleteUser({
						id: targetUser.id,
						name: targetUser.name,
						kidId: targetUser.kidId,
						description: targetUser.description,
						gender: targetUser.gender,
						birth: targetUser.birth,
					});
				} catch (error) {
					failedCount += 1;
					console.error('Failed to delete user:', targetUser?.id, error);
				}
			}

			await refetch();
			setSelectedUserIds([]);

			if (failedCount === 0) {
				alert(`${selectedUsers.length}명의 친구 정보를 삭제했습니다.`);
			} else {
				const successCount = selectedUsers.length - failedCount;
				alert(
					`선택 ${selectedUsers.length}명 중 ${successCount}명 삭제, ${failedCount}명 실패했습니다.`,
				);
			}
		} catch (error) {
			console.error('Failed to delete selected users:', error);
			alert('선택 삭제 중 오류가 발생했습니다.');
		} finally {
			setIsDeletingSelected(false);
		}
	};

	// 검색어/검색타입 변경 시 페이지 리셋
	useEffect(() => {
		setCurrentPage(1);
	}, [debouncedSearch, searchType]);

	const searchPlaceholder =
		searchType === 'name' ? '이름을 입력해 주세요.' : '반을 입력해 주세요.';

	const handleToggleUserSelect = useCallback((userId) => {
		const normalizedId = String(userId);
		setSelectedUserIds((prev) =>
			prev.includes(normalizedId)
				? prev.filter((id) => id !== normalizedId)
				: [...prev, normalizedId],
		);
	}, []);

	const handleToggleAllSelect = useCallback((targetIds, checked) => {
		setSelectedUserIds((prev) => {
			const next = new Set(prev.map(String));
			targetIds.map(String).forEach((id) => {
				if (checked) next.add(id);
				else next.delete(id);
			});
			return Array.from(next);
		});
	}, []);

	return (
		<S.Container $fullWidth={!!user}>
			{!user && (
				<S.DateAttendanceWrap>
					<S.SearchFilterBar>
						<select
							value={searchType}
							onChange={(e) => setSearchType(e.target.value)}>
							<option value='name'>이름</option>
							<option value='class'>반</option>
						</select>
						<S.SearchBar>
							<Image srcArray={S.search_list} />
							<input
								type={'text'}
								placeholder={searchPlaceholder}
								value={searchInput}
								onChange={(e) => {
									setSearchInput(e.target.value);
								}}
							/>
						</S.SearchBar>

						<S.HeaderButton>
							<S.AddButton
								className='privacy'
								onClick={async () => {
									await downloadPrivacy(
										'https://torooc-storage-fastapi.s3.ap-northeast-2.amazonaws.com/liku/contents/material/%EA%B0%9C%EC%9D%B8%EC%A0%95%EB%B3%B4%EC%88%98%EC%A7%91%EB%B0%8F%EC%9D%B4%EC%9A%A9%EB%8F%99%EC%9D%98%EC%84%9C/%E1%84%80%E1%85%A2%E1%84%8B%E1%85%B5%E1%86%AB%E1%84%8C%E1%85%A5%E1%86%BC%E1%84%87%E1%85%A9%E1%84%89%E1%85%AE%E1%84%8C%E1%85%B5%E1%86%B8%E1%84%86%E1%85%B5%E1%86%BE%E1%84%8B%E1%85%B5%E1%84%8B%E1%85%AD%E1%86%BC%E1%84%83%E1%85%A9%E1%86%BC%E1%84%8B%E1%85%B4%E1%84%89%E1%85%A5_240823.pdf',
									);
								}}>
								개인정보수집 및 이용 동의서
							</S.AddButton>
						</S.HeaderButton>
						<S.HeaderButton>
							<S.AddButton onClick={() => setUser({ type: 'add' })}>
								친구 추가
							</S.AddButton>
						</S.HeaderButton>
						<S.HeaderButton>
							<S.AddButton
								onClick={handleDeleteSelectedUsers}
								disabled={isDeletingSelected}>
								선택 삭제
							</S.AddButton>
						</S.HeaderButton>
					</S.SearchFilterBar>

					<S.Data>
						<button
							className='month'
							onClick={() => {
								setMonth(false);
							}}>
							<div>
								<Image srcArray={S.arrow_left_list} />
								<Image srcArray={S.arrow_left_list} />
							</div>
						</button>
						<button
							className='days'
							onClick={() => {
								setDays(false);
							}}>
							<Image srcArray={S.arrow_left_list} />
						</button>
						<span>{date.getFormatDate()}</span>
						<button
							className='days'
							onClick={() => {
								setDays(true);
							}}
							disabled={date.isFutureDate('day')}>
							<Image srcArray={S.arrow_right_list} />
						</button>
						<button
							className='month'
							onClick={() => {
								setMonth(true);
							}}
							disabled={date.isFutureDate('month')}>
							<div>
								<Image srcArray={S.arrow_right_list} />
								<Image srcArray={S.arrow_right_list} />
							</div>
						</button>
					</S.Data>

					<AttendanceTable
						attendance={ATTENDANCE_STATE}
						attendanceData={attendance}
						date={date}
						setUser={setUser.bind(this)}
						filter={''}
						loading={loading}
						selectedUserIds={selectedUserIds}
						onToggleUserSelect={handleToggleUserSelect}
						onToggleAllSelect={handleToggleAllSelect}
					/>

					<S.PageSizeSelector>
						{pageInfo && (
							<Pagination
								currentPage={currentPage}
								totalPages={pageInfo.totalPages || 1}
								onPageChange={handlePageChange}
								showPageInfo={true}
							/>
						)}
						<div className='size-selector'>
							<label>표시 개수</label>
							<select value={pageSize} onChange={handlePageSizeChange}>
								<option value={10}>10개</option>
								<option value={20}>20개</option>
								<option value={30}>30개</option>
								<option value={50}>50개</option>
							</select>
							{searchInput && (
								<span className='search-info'>
									&quot;{searchInput}&quot; 검색 결과
								</span>
							)}
						</div>
					</S.PageSizeSelector>
				</S.DateAttendanceWrap>
			)}
			<S.InfoWrap>
				{!user && (
					<TotalAttendance
						initAttendance={ATTENDANCE_STATE}
						total={pageInfo?.totalCount || 0}
						attendanceData={attendance}
						attendanceCount={pageInfo?.attendanceCount || 0}
						absenceCount={pageInfo?.absenceCount || 0}
					/>
				)}
				{user && (
					<UserInfo
						user={user?.value}
						type={user?.type}
						setUser={setUser.bind(this)}
						refetch={refetch}
					/>
				)}
			</S.InfoWrap>
		</S.Container>
	);
});

export default PromptManagePage;
